import type { Components } from '@/api/generated/api';
import {
  changeCompetenceAssessmentVisual,
  fetchCompetenceAssessmentsAdmin,
  fetchCompetenceAssessmentsTeacher,
} from '@/api/resources/competenceAssessment.api';
import { usePermissions } from '@/composables/usePermissions';
import { useQueryWithToast } from '@/composables/useQueryWithToast';
import { useToast } from '@/composables/useToast';
import { useCurrentClass } from '@/schoolClasses/useCurrentClass';
import { gradeCalculationKeys } from '../gradeCalculation/gradeCalculation.keys';
import { useQueryClient } from '@tanstack/vue-query';
import { computed, type ComputedRef } from 'vue';
import { competenceAssessmentKeys } from './competenceAssessment.keys';

interface ICompetenceAssessmentComposable {
  allCompetenceAssessmentAdmin: ComputedRef<Components.Schemas.CompetenceAssessmentDto[]>;
  allCompetenceAssessmentTeacher: ComputedRef<Components.Schemas.CompetenceAssessmentDto[]>;
  isTeacherAssessmentsResolved: ComputedRef<boolean>;
  currentCompetenceAssessment: ComputedRef<Components.Schemas.CompetenceAssessmentDto | null>;
  changeVisual: (
    competenceAssessmentId: number,
    dto: Components.Schemas.ChangeCompetenceAssessmentVisualDto,
  ) => Promise<void>;
}

export const useCompetenceAssessment = (): ICompetenceAssessmentComposable => {
  const { hasTenantAdminAccess } = usePermissions();
  const { currentClass } = useCurrentClass();
  const { editSuccessToast, catchError } = useToast();
  const queryClient = useQueryClient();

  const classId = computed(() => currentClass.value?.id);

  const adminEnabled = computed(() => Boolean(hasTenantAdminAccess.value));
  const adminKey = computed(() => competenceAssessmentKeys.adminList(adminEnabled.value));

  const adminQuery = useQueryWithToast({
    queryKey: adminKey,
    enabled: adminEnabled,
    queryFn: fetchCompetenceAssessmentsAdmin,
  });

  const teacherEnabled = computed(() => Boolean(classId.value));
  const teacherKey = computed(() => competenceAssessmentKeys.teacherList(classId.value));

  const teacherQuery = useQueryWithToast({
    queryKey: teacherKey,
    enabled: teacherEnabled,
    queryFn: () => fetchCompetenceAssessmentsTeacher({ classId: classId.value! }),
  });

  const allCompetenceAssessmentAdmin = computed(() => adminQuery.data.value ?? []);
  const allCompetenceAssessmentTeacher = computed(() => teacherQuery.data.value ?? []);
  const isTeacherAssessmentsResolved = computed(() => teacherQuery.isFetched.value);

  const currentCompetenceAssessment = computed(() => {
    const list = allCompetenceAssessmentTeacher.value;
    return list.length ? list[0] : null;
  });

  const invalidateAllCompetenceAssessmentLists = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: competenceAssessmentKeys.lists() }),
      queryClient.invalidateQueries({ queryKey: gradeCalculationKeys.all }),
    ]);
  };

  const changeVisual = async (
    competenceAssessmentId: number,
    dto: Components.Schemas.ChangeCompetenceAssessmentVisualDto,
  ): Promise<void> => {
    await catchError(async () => {
      await changeCompetenceAssessmentVisual({ competenceAssessmentId, dto });
      await invalidateAllCompetenceAssessmentLists();
      editSuccessToast();
    });
  };

  return {
    allCompetenceAssessmentAdmin,
    allCompetenceAssessmentTeacher,
    isTeacherAssessmentsResolved,
    currentCompetenceAssessment,
    changeVisual,
  };
};
